import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Badge,
  useDisclosure,
  Spinner,
  Input,
  Textarea,
  NativeSelect,
  Checkbox,
  Card,
  Tabs,
  Icon,
  Tag,
  Progress,
  Alert,
  Dialog,
  Portal,
  Separator,
  Field,
} from '@chakra-ui/react';
import { TabList, Tab, TabPanels, TabPanel, Switch} from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputRightElement } from '@/components/ui/input-group';
import { Tooltip } from '@/components/ui/tooltip';
import { IsolateCheckboxesFromField } from '@/components/ui/multi-checkbox-field';
import {
  FaPlus, FaEdit, FaTrash, FaPoll, FaLink, FaCopy,
  FaChevronLeft, FaChevronRight, FaUsers, FaCheck, FaTimes, FaImage, FaArrowUp, FaArrowDown,
  FaFilePdf, FaLock, FaLockOpen, FaEye, FaSearch, FaHistory,
} from 'react-icons/fa';
import { encuestasConsultasAPI, musicosAPI } from '../services/api';
import AppLayout from './AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { useProductTour } from '../hooks/useProductTour';
import { bandaEncuestasSteps } from '../tours/bandaEncuestasTour';

// ─── Constantes ──────────────────────────────────────────────────────────────

const ESTADO_CONFIG = {
  BORRADOR: { label: 'Borrador', color: 'gray', icon: FaEdit },
  ABIERTA:  { label: 'Abierta',  color: 'green', icon: FaLockOpen },
  CERRADA:  { label: 'Cerrada',  color: 'red', icon: FaLock },
};

const TIPO_PREGUNTA_LABELS = {
  TEXTO_LIBRE:       'Texto libre',
  SELECCION_UNICA:   'Selección única',
  SELECCION_MULTIPLE: 'Selección múltiple',
};

const EMPTY_PREGUNTA = () => ({
  _key: Date.now() + Math.random(),
  texto: '',
  tipoPregunta: 'SELECCION_UNICA',
  obligatoria: true,
  opciones: [{ texto: '', _imagenes: [] }, { texto: '', _imagenes: [] }],
  _imagenes: [],
});

const EMPTY_FORM = () => ({
  titulo: '',
  introduccion: '',
  fechaInicio: new Date().toISOString().slice(0, 10),
  fechaFin: '',
  anonima: false,
  password: '',
  preguntas: [EMPTY_PREGUNTA()],
  convocados: [],
});

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ThumbWithLightbox({ src, size = '80px', borderRadius = '8px', children }) {
  const [open, setOpen] = useState(false);
  return (
    <Box position="relative" w={size} h={size}>
      <img
        src={src} alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius, cursor: 'pointer' }}
        onClick={() => setOpen(true)}
      />
      {children}
      <Dialog.Root open={open} size='xl' placement='center' onOpenChange={e => {
        if (!e.open) {
          setOpen(false);
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.700" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" shadow="none" maxW="90vw">
              <Dialog.CloseTrigger color="white" bg="blackAlpha.600" rounded="full" top={-3} right={-3} zIndex={2} />
              <Dialog.Body p={0} display="flex" justifyContent="center">
                <img
                  src={src} alt=""
                  style={{ maxHeight: '85vh', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }}
                />
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </Box>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function EncuestasConsultas() {
  const { user } = useAuth();
  useProductTour('banda-encuestas', bandaEncuestasSteps, { autoStart: false });
  
  const bg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleBg = useColorModeValue('gray.50', 'gray.900');
  const cancelRef = useRef();

  // Data
  const [encuestas, setEncuestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEncuesta, setSelectedEncuesta] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [resultadosLoading, setResultadosLoading] = useState(false);
  const [musicos, setMusicos] = useState([]);
  const [gruposMusicos, setGruposMusicos] = useState([]);

  // Modals
  const { open: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { open: isEstadoOpen, onOpen: onEstadoOpen, onClose: onEstadoClose } = useDisclosure();

  // Form
  const [form, setForm] = useState(EMPTY_FORM());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formTab, setFormTab] = useState(0);

  // Delete / estado change
  const [deletingId, setDeletingId] = useState(null);
  const [estadoChange, setEstadoChange] = useState(null); // { id, nuevoEstado }

  // PDF
  const [pdfLoading, setPdfLoading] = useState(false);

  // ─── Data loading ────────────────────────────────────────────────────────

  const fetchEncuestas = useCallback(async () => {
    try {
      const res = await encuestasConsultasAPI.getAll();
      setEncuestas(res.data ?? []);
    } catch {
      toaster.create({ title: 'Error al cargar encuestas', type: 'error', duration: 3000 });
    }
  }, []);

  const fetchMusicos = useCallback(async () => {
    try {
      const [mRes, gRes] = await Promise.all([
        musicosAPI.getAll(),
        musicosAPI.getGrupos(),
      ]);
      setMusicos(mRes.data ?? []);
      setGruposMusicos(gRes.data ?? []);
    } catch {
      /* silent */
    }
  }, []);

  const fetchResultados = useCallback(async (id) => {
    setResultadosLoading(true);
    try {
      const res = await encuestasConsultasAPI.getResultados(id);
      setResultados(res.data);
    } catch {
      setResultados(null);
    } finally {
      setResultadosLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchEncuestas(), fetchMusicos()]);
      setLoading(false);
    };
    load();
  }, [fetchEncuestas, fetchMusicos]);

  const refreshDetail = useCallback(async (id) => {
    try {
      const res = await encuestasConsultasAPI.getOne(id);
      setSelectedEncuesta(res.data);
      fetchResultados(id);
    } catch {
      toaster.create({ title: 'Error al cargar detalle', type: 'error', duration: 3000 });
    }
  }, [fetchResultados]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM());
    setFormTab(0);
    onFormOpen();
  };

  const openEdit = async (enc) => {
    let full = enc;
    if (!enc.preguntas) {
      try {
        const res = await encuestasConsultasAPI.getOne(enc.id);
        full = res.data;
      } catch {
        toaster.create({ title: 'Error al cargar encuesta', type: 'error', duration: 3000 });
        return;
      }
    }
    setEditingId(full.id);
    setForm({
      titulo: full.titulo || '',
      introduccion: full.introduccion || '',
      fechaInicio: full.fechaInicio ? full.fechaInicio.slice(0, 10) : '',
      fechaFin: full.fechaFin ? full.fechaFin.slice(0, 10) : '',
      anonima: full.anonima || false,
      password: full.password || '',
      preguntas: (full.preguntas || []).map((p) => ({
        _key: p.id || Date.now() + Math.random(),
        _existingId: p.id,
        texto: p.texto || '',
        tipoPregunta: p.tipoPregunta || 'SELECCION_UNICA',
        obligatoria: p.obligatoria !== false,
        opciones: (p.opciones || []).map((o) => ({
          texto: o.texto || '',
          _existingId: o.id,
          _imagenes: [],
          _existingImages: (o.imagenes || []).map((img) => ({ id: img.id, url: img.url })),
        })),
        _imagenes: [],
        _existingImages: (p.imagenes || []).map((img) => ({ id: img.id, url: img.url })),
      })),
      convocados: (full.convocados || []).map((c) => c.musicoId || c.musico?.id).filter(Boolean),
    });
    setFormTab(0);
    onFormOpen();
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const buildPayload = () => {
    const p = {
      titulo: form.titulo.trim(),
      introduccion: form.introduccion.trim() || undefined,
      fechaInicio: form.fechaInicio || undefined,
      fechaFin: form.fechaFin || undefined,
      anonima: form.anonima,
      preguntas: form.preguntas.map((pr) => {
        const pregObj = {
          texto: pr.texto.trim(),
          tipoPregunta: pr.tipoPregunta,
          obligatoria: pr.obligatoria,
          opciones: (pr.tipoPregunta !== 'TEXTO_LIBRE' ? pr.opciones : [])
            .filter((o) => o.texto.trim())
            .map((o) => {
              const opObj = { texto: o.texto.trim() };
              const existImgIds = (o._existingImages || []).map((img) => img.id);
              if (existImgIds.length) opObj.existingImagenIds = existImgIds;
              return opObj;
            }),
        };
        const existImgIds = (pr._existingImages || []).map((img) => img.id);
        if (existImgIds.length) pregObj.existingImagenIds = existImgIds;
        return pregObj;
      }),
      convocadoIds: form.convocados,
    };
    if (form.password?.trim()) p.password = form.password.trim();
    return p;
  };

  const validateForm = () => {
    if (!form.titulo.trim() || !form.fechaInicio) return false;
    if (form.preguntas.length === 0) return false;
    for (const p of form.preguntas) {
      if (!p.texto.trim()) return false;
      if (p.tipoPregunta !== 'TEXTO_LIBRE') {
        if ((p.opciones || []).filter((o) => o.texto.trim()).length < 2) return false;
      }
    }
    if (form.convocados.length === 0) return false;
    return true;
  };

  const uploadPendingImages = async (encuestaId, savedPreguntas) => {
    for (let i = 0; i < form.preguntas.length; i++) {
      const localP = form.preguntas[i];
      const savedP = savedPreguntas[i];
      if (!savedP) continue;

      for (const file of (localP._imagenes || [])) {
        const fd = new FormData();
        fd.append('file', file);
        await encuestasConsultasAPI.uploadImagenPregunta(encuestaId, savedP.id, fd);
      }

      if (savedP.opciones) {
        const filteredLocal = (localP.tipoPregunta !== 'TEXTO_LIBRE' ? localP.opciones : [])
          .filter((o) => o.texto.trim());
        for (let j = 0; j < filteredLocal.length; j++) {
          const localO = filteredLocal[j];
          const savedO = savedP.opciones[j];
          if (!savedO) continue;
          for (const file of (localO._imagenes || [])) {
            const fd = new FormData();
            fd.append('file', file);
            await encuestasConsultasAPI.uploadImagenOpcion(encuestaId, savedO.id, fd);
          }
        }
      }
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      let saved;
      if (editingId) {
        const res = await encuestasConsultasAPI.update(editingId, payload);
        saved = res.data;
        const hasPendingImages = form.preguntas.some(
          (p) => (p._imagenes || []).length > 0 || (p.opciones || []).some((o) => (o._imagenes || []).length > 0),
        );
        if (hasPendingImages && saved?.preguntas) {
          await uploadPendingImages(editingId, saved.preguntas);
        }
        toaster.create({ title: 'Encuesta actualizada', type: 'success', duration: 3000 });
        if (selectedEncuesta?.id === editingId) refreshDetail(editingId);
      } else {
        const res = await encuestasConsultasAPI.create(payload);
        saved = res.data;
        const hasPendingImages = form.preguntas.some(
          (p) => (p._imagenes || []).length > 0 || (p.opciones || []).some((o) => (o._imagenes || []).length > 0),
        );
        if (hasPendingImages && saved?.preguntas) {
          await uploadPendingImages(saved.id, saved.preguntas);
        }
        toaster.create({ title: 'Encuesta creada', type: 'success', duration: 3000 });
      }
      onFormClose();
      fetchEncuestas();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al guardar';
      toaster.create({ title: msg, type: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id) => {
    setDeletingId(id);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await encuestasConsultasAPI.remove(deletingId);
      toaster.create({ title: 'Encuesta eliminada', type: 'info', duration: 3000 });
      if (selectedEncuesta?.id === deletingId) setSelectedEncuesta(null);
      onDeleteClose();
      fetchEncuestas();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al eliminar';
      toaster.create({ title: msg, type: 'error', duration: 4000 });
    }
  };

  const confirmEstadoChange = (id, nuevoEstado) => {
    setEstadoChange({ id, nuevoEstado });
    onEstadoOpen();
  };

  const handleChangeEstado = async () => {
    if (!estadoChange) return;
    try {
      await encuestasConsultasAPI.changeEstado(estadoChange.id, estadoChange.nuevoEstado);
      const label = estadoChange.nuevoEstado === 'ABIERTA' ? 'abierta' : 'cerrada';
      toaster.create({ title: `Encuesta ${label}`, type: 'success', duration: 3000 });
      onEstadoClose();
      fetchEncuestas();
      if (selectedEncuesta?.id === estadoChange.id) refreshDetail(estadoChange.id);
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al cambiar estado';
      toaster.create({ title: msg, type: 'error', duration: 4000 });
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      toaster.create({ title: 'Enlace copiado', type: 'success', duration: 2000 });
    }).catch(() => {
      toaster.create({ title: 'Error al copiar', type: 'error', duration: 2000 });
    });
  };

  const handleDownloadPdf = async (id) => {
    setPdfLoading(true);
    try {
      const res = await encuestasConsultasAPI.getResultadosPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resultados-encuesta-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toaster.create({ title: 'PDF descargado', type: 'success', duration: 2000 });
    } catch {
      toaster.create({ title: 'Error al descargar PDF', type: 'error', duration: 3000 });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleUploadImage = async (type, id, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (type === 'pregunta') {
        await encuestasConsultasAPI.uploadImagenPregunta(selectedEncuesta.id, id, formData);
      } else {
        await encuestasConsultasAPI.uploadImagenOpcion(selectedEncuesta.id, id, formData);
      }
      toaster.create({ title: 'Imagen subida', type: 'success', duration: 2000 });
      refreshDetail(selectedEncuesta.id);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al subir imagen';
      toaster.create({ title: msg, type: 'error', duration: 4000 });
    }
  };

  const handleDeleteImage = async (imagenId) => {
    try {
      await encuestasConsultasAPI.deleteImagen(imagenId);
      toaster.create({ title: 'Imagen eliminada', type: 'success', duration: 2000 });
      refreshDetail(selectedEncuesta.id);
    } catch {
      toaster.create({ title: 'Error al eliminar imagen', type: 'error', duration: 3000 });
    }
  };

  // ─── Pregunta helpers ────────────────────────────────────────────────────

  const addPregunta = () => {
    setForm((prev) => ({
      ...prev,
      preguntas: [...prev.preguntas, EMPTY_PREGUNTA()],
    }));
  };

  const removePregunta = (idx) => {
    setForm((prev) => ({
      ...prev,
      preguntas: prev.preguntas.filter((_, i) => i !== idx),
    }));
  };

  const updatePregunta = (idx, field, value) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      preguntas[idx] = { ...preguntas[idx], [field]: value };
      if (field === 'tipoPregunta' && value !== 'TEXTO_LIBRE' && (!preguntas[idx].opciones || preguntas[idx].opciones.length < 2)) {
        preguntas[idx].opciones = [{ texto: '' }, { texto: '' }];
      }
      return { ...prev, preguntas };
    });
  };

  const movePregunta = (idx, direction) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= preguntas.length) return prev;
      [preguntas[idx], preguntas[newIdx]] = [preguntas[newIdx], preguntas[idx]];
      return { ...prev, preguntas };
    });
  };

  const addOpcion = (pIdx) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      preguntas[pIdx] = {
        ...preguntas[pIdx],
        opciones: [...(preguntas[pIdx].opciones || []), { texto: '', _imagenes: [] }],
      };
      return { ...prev, preguntas };
    });
  };

  const removeOpcion = (pIdx, oIdx) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      preguntas[pIdx] = {
        ...preguntas[pIdx],
        opciones: preguntas[pIdx].opciones.filter((_, i) => i !== oIdx),
      };
      return { ...prev, preguntas };
    });
  };

  const updateOpcion = (pIdx, oIdx, value) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      const opciones = [...preguntas[pIdx].opciones];
      opciones[oIdx] = { ...opciones[oIdx], texto: value };
      preguntas[pIdx] = { ...preguntas[pIdx], opciones };
      return { ...prev, preguntas };
    });
  };

  const addImageToPregunta = (pIdx, file) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      preguntas[pIdx] = {
        ...preguntas[pIdx],
        _imagenes: [...(preguntas[pIdx]._imagenes || []), file],
      };
      return { ...prev, preguntas };
    });
  };

  const removeImageFromPregunta = (pIdx, imgIdx) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      preguntas[pIdx] = {
        ...preguntas[pIdx],
        _imagenes: (preguntas[pIdx]._imagenes || []).filter((_, i) => i !== imgIdx),
      };
      return { ...prev, preguntas };
    });
  };

  const removeExistingImageFromPregunta = async (pIdx, imgId) => {
    try {
      await encuestasConsultasAPI.deleteImagen(imgId);
      setForm((prev) => {
        const preguntas = [...prev.preguntas];
        preguntas[pIdx] = {
          ...preguntas[pIdx],
          _existingImages: (preguntas[pIdx]._existingImages || []).filter((img) => img.id !== imgId),
        };
        return { ...prev, preguntas };
      });
    } catch {
      toaster.create({ title: 'Error al eliminar imagen', type: 'error', duration: 3000 });
    }
  };

  const addImageToOpcion = (pIdx, oIdx, file) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      const opciones = [...preguntas[pIdx].opciones];
      opciones[oIdx] = {
        ...opciones[oIdx],
        _imagenes: [...(opciones[oIdx]._imagenes || []), file],
      };
      preguntas[pIdx] = { ...preguntas[pIdx], opciones };
      return { ...prev, preguntas };
    });
  };

  const removeImageFromOpcion = (pIdx, oIdx, imgIdx) => {
    setForm((prev) => {
      const preguntas = [...prev.preguntas];
      const opciones = [...preguntas[pIdx].opciones];
      opciones[oIdx] = {
        ...opciones[oIdx],
        _imagenes: (opciones[oIdx]._imagenes || []).filter((_, i) => i !== imgIdx),
      };
      preguntas[pIdx] = { ...preguntas[pIdx], opciones };
      return { ...prev, preguntas };
    });
  };

  const removeExistingImageFromOpcion = async (pIdx, oIdx, imgId) => {
    try {
      await encuestasConsultasAPI.deleteImagen(imgId);
      setForm((prev) => {
        const preguntas = [...prev.preguntas];
        const opciones = [...preguntas[pIdx].opciones];
        opciones[oIdx] = {
          ...opciones[oIdx],
          _existingImages: (opciones[oIdx]._existingImages || []).filter((img) => img.id !== imgId),
        };
        preguntas[pIdx] = { ...preguntas[pIdx], opciones };
        return { ...prev, preguntas };
      });
    } catch {
      toaster.create({ title: 'Error al eliminar imagen', type: 'error', duration: 3000 });
    }
  };

  // ─── Detail view open ───────────────────────────────────────────────────

  const openDetail = async (enc) => {
    setSelectedEncuesta(enc);
    refreshDetail(enc.id);
  };

  // ─── Computed ────────────────────────────────────────────────────────────

  const sortedEncuestas = useMemo(() => {
    return [...encuestas].sort((a, b) => {
      const order = { ABIERTA: 0, BORRADOR: 1, CERRADA: 2 };
      const diff = (order[a.estado] ?? 3) - (order[b.estado] ?? 3);
      if (diff !== 0) return diff;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [encuestas]);

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <AppLayout userType="banda">
        <Flex justify="center" align="center" minH="60vh">
          <Spinner size="xl" color="purple.500" borderWidth="4px" />
        </Flex>
      </AppLayout>
    );
  }

  return (
    <AppLayout userType="banda">
      <Box maxW="6xl" mx="auto" px={{ base: 3, md: 6 }} py={6}>
        {selectedEncuesta ? (
          <DetailView
            encuesta={selectedEncuesta}
            resultados={resultados}
            resultadosLoading={resultadosLoading}
            musicos={musicos}
            onBack={() => { setSelectedEncuesta(null); fetchEncuestas(); }}
            onEdit={openEdit}
            onDelete={confirmDelete}
            onChangeEstado={confirmEstadoChange}
            onCopyLink={handleCopyLink}
            onDownloadPdf={handleDownloadPdf}
            onUploadImage={handleUploadImage}
            onDeleteImage={handleDeleteImage}
            pdfLoading={pdfLoading}
            cardBg={cardBg}
            borderColor={borderColor}
            subtleBg={subtleBg}
          />
        ) : (
          <ListView
            encuestas={sortedEncuestas}
            onOpenCreate={openCreate}
            onOpenDetail={openDetail}
            onEdit={openEdit}
            onDelete={confirmDelete}
            cardBg={cardBg}
            borderColor={borderColor}
          />
        )}
      </Box>

      {/* Create/Edit Modal */}
      <FormModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        form={form}
        setField={setField}
        formTab={formTab}
        setFormTab={setFormTab}
        editingId={editingId}
        saving={saving}
        onSave={handleSave}
        musicos={musicos}
        gruposMusicos={gruposMusicos}
        preguntas={form.preguntas}
        addPregunta={addPregunta}
        removePregunta={removePregunta}
        updatePregunta={updatePregunta}
        movePregunta={movePregunta}
        addOpcion={addOpcion}
        removeOpcion={removeOpcion}
        updateOpcion={updateOpcion}
        addImageToPregunta={addImageToPregunta}
        removeImageFromPregunta={removeImageFromPregunta}
        removeExistingImageFromPregunta={removeExistingImageFromPregunta}
        addImageToOpcion={addImageToOpcion}
        removeImageFromOpcion={removeImageFromOpcion}
        removeExistingImageFromOpcion={removeExistingImageFromOpcion}
      />

      {/* Delete Confirm */}
      <Dialog.Root
        open={isDeleteOpen}
        initialFocusEl={() => cancelRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDeleteClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">Eliminar encuesta</Dialog.Header>
                <Dialog.Body>
                  ¿Estás seguro? Se eliminarán todas las preguntas, respuestas y convocados. Esta acción no se puede deshacer.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={onDeleteClose}>Cancelar</Button>
                  <Button colorPalette="red" onClick={handleDelete} ml={3}>Eliminar</Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Estado Change Confirm */}
      <Dialog.Root
        open={isEstadoOpen}
        initialFocusEl={() => cancelRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onEstadoClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  {estadoChange?.nuevoEstado === 'ABIERTA' ? 'Abrir encuesta' : 'Cerrar encuesta'}
                </Dialog.Header>
                <Dialog.Body>
                  {estadoChange?.nuevoEstado === 'ABIERTA'
                    ? 'Al abrir la encuesta, los músicos convocados podrán acceder y responder. Ya no podrás editar las preguntas.'
                    : 'Al cerrar la encuesta, no se aceptarán más respuestas. Podrás consultar los resultados.'}
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={onEstadoClose}>Cancelar</Button>
                  <Button
                    colorPalette={estadoChange?.nuevoEstado === 'ABIERTA' ? 'green' : 'red'}
                    onClick={handleChangeEstado}
                    ml={3}
                  >
                    {estadoChange?.nuevoEstado === 'ABIERTA' ? 'Abrir' : 'Cerrar'}
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </AppLayout>
  );
}

// ─── ListView ────────────────────────────────────────────────────────────────

function ListView({ encuestas, onOpenCreate, onOpenDetail, onEdit, onDelete, cardBg, borderColor }) {
  const activas = encuestas.filter((e) => e.estado !== 'CERRADA');
  const cerradas = encuestas.filter((e) => e.estado === 'CERRADA');

  const [histSearch, setHistSearch] = useState('');
  const [histSort, setHistSort] = useState('reciente');

  const cerradasFiltered = useMemo(() => {
    let list = cerradas;
    if (histSearch.trim()) {
      const q = histSearch.toLowerCase().trim();
      list = list.filter((e) => e.titulo.toLowerCase().includes(q));
    }
    if (histSort === 'reciente') {
      list = [...list].sort((a, b) => new Date(b.fechaFin || b.fechaInicio) - new Date(a.fechaFin || a.fechaInicio));
    } else if (histSort === 'antiguo') {
      list = [...list].sort((a, b) => new Date(a.fechaFin || a.fechaInicio) - new Date(b.fechaFin || b.fechaInicio));
    } else if (histSort === 'az') {
      list = [...list].sort((a, b) => a.titulo.localeCompare(b.titulo, 'es'));
    } else if (histSort === 'za') {
      list = [...list].sort((a, b) => b.titulo.localeCompare(a.titulo, 'es'));
    }
    return list;
  }, [cerradas, histSearch, histSort]);

  return (
    <>
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3} data-tour="encuestas-header">
        <HStack gap={3}>
          <Icon color="teal.500" boxSize={6} asChild><FaPoll /></Icon>
          <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold">Encuestas y consultas</Text>
        </HStack>
        <Button
          colorPalette="teal"
          size="sm"
          rounded="full"
          onClick={onOpenCreate}
          data-tour="encuestas-crear"><FaPlus />Crear encuesta
                  </Button>
      </Flex>

      <Tabs.Root variant='subtle' colorPalette="teal" size="sm" defaultValue="0">
        <Tabs.List mb={4} gap={1} data-tour="encuestas-tabs">
          <Tab value="0" fontWeight="600">
            <Icon mr={2} asChild><FaPoll /></Icon> Activas
            {activas.length > 0 && (
              <Badge ml={2} colorPalette="teal" rounded="full" fontSize="2xs">{activas.length}</Badge>
            )}
          </Tab>
          <Tab value="1" fontWeight="600">
            <Icon mr={2} asChild><FaHistory /></Icon> Histórico
            {cerradas.length > 0 && (
              <Badge ml={2} colorPalette="gray" rounded="full" fontSize="2xs">{cerradas.length}</Badge>
            )}
          </Tab>
        </Tabs.List>
        <TabPanels>
          {/* Activas */}
          <TabPanel value="0" px={0}>
            {activas.length === 0 ? (
              <EmptyState onOpenCreate={onOpenCreate} />
            ) : (
              <VStack gap={3} align="stretch">
                {activas.map((enc) => (
                  <EncuestaCard
                    key={enc.id}
                    enc={enc}
                    onOpen={() => onOpenDetail(enc)}
                    onEdit={() => onEdit(enc)}
                    onDelete={() => onDelete(enc.id)}
                    cardBg={cardBg}
                    borderColor={borderColor}
                  />
                ))}
              </VStack>
            )}
          </TabPanel>
          {/* Histórico */}
          <TabPanel value="1" px={0}>
            <Flex gap={3} mb={4} wrap="wrap">
              <InputGroup size="sm" maxW="280px">
                <Input
                  placeholder="Buscar por nombre…"
                  rounded="full"
                  value={histSearch}
                  onChange={(e) => setHistSearch(e.target.value)}
                />
                <InputRightElement>
                  <Icon color="gray.400" boxSize={3} asChild><FaSearch /></Icon>
                </InputRightElement>
              </InputGroup>
              <NativeSelect.Root>
                <NativeSelect.Field
                  size="sm"
                  rounded="full"
                  maxW="200px"
                  value={histSort}
                  onChange={(e) => setHistSort(e.target.value)}>
                  <option value="reciente">Más reciente</option>
                  <option value="antiguo">Más antiguo</option>
                  <option value="az">A → Z</option>
                  <option value="za">Z → A</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Flex>
            {cerradasFiltered.length === 0 ? (
              <Flex direction="column" align="center" py={10}>
                <Icon boxSize={10} color="gray.300" mb={3} asChild><FaHistory /></Icon>
                <Text color="gray.400" fontSize="sm">
                  {cerradas.length === 0
                    ? 'No hay encuestas cerradas en el histórico'
                    : 'No se encontraron encuestas con ese nombre'}
                </Text>
              </Flex>
            ) : (
              <VStack gap={3} align="stretch">
                {cerradasFiltered.map((enc) => (
                  <EncuestaCard
                    key={enc.id}
                    enc={enc}
                    onOpen={() => onOpenDetail(enc)}
                    onEdit={() => onEdit(enc)}
                    onDelete={null}
                    cardBg={cardBg}
                    borderColor={borderColor}
                  />
                ))}
              </VStack>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs.Root>
    </>
  );
}

function EmptyState({ onOpenCreate }) {
  const emptyBg = useColorModeValue('gray.50', 'gray.800');
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      py={16}
      px={6}
      bg={emptyBg}
      rounded="2xl"
      border="2px dashed"
      borderColor="gray.300"
    >
      <Icon boxSize={12} color="gray.300" mb={4} asChild><FaPoll /></Icon>
      <Text fontSize="lg" fontWeight="600" color="gray.500" mb={2}>
        No hay encuestas creadas
      </Text>
      <Text fontSize="sm" color="gray.400" mb={6} textAlign="center" maxW="sm">
        Crea encuestas y consultas para recoger la opinión de tus músicos de forma sencilla.
      </Text>
      <Button colorPalette="teal" rounded="full" onClick={onOpenCreate}><FaPlus />Crear primera encuesta
              </Button>
    </Flex>
  );
}

// ─── EncuestaCard ────────────────────────────────────────────────────────────

function EncuestaCard({ enc, onOpen, onEdit, onDelete, cardBg, borderColor }) {
  const estado = ESTADO_CONFIG[enc.estado] || ESTADO_CONFIG.BORRADOR;
  const totalConvocados = enc.totalConvocados ?? enc.convocados?.length ?? 0;
  const respondidos = enc.totalRespondidos ?? 0;
  const pct = totalConvocados > 0 ? Math.round((respondidos / totalConvocados) * 100) : 0;

  return (
    <Card.Root
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      rounded="xl"
      cursor="pointer"
      onClick={onOpen}
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-1px)' }}
    >
      <Card.Body py={4} px={5}>
        <Flex justify="space-between" align="start" gap={3} wrap="wrap">
          <Box flex={1} minW={0}>
            <HStack gap={2} mb={1} wrap="wrap">
              <Text fontSize="md" fontWeight="700" lineClamp={1}>
                {enc.titulo}
              </Text>
              <Badge colorPalette={estado.color} fontSize="2xs" rounded="full" px={2}>
                {estado.label}
              </Badge>
              {enc.anonima && (
                <Badge colorPalette="purple" variant="subtle" fontSize="2xs" rounded="full" px={2}>
                  Anónima
                </Badge>
              )}
            </HStack>
            <HStack gap={3} fontSize="xs" color="gray.500" wrap="wrap">
              <Text>{formatDate(enc.fechaInicio)}</Text>
              <Text>—</Text>
              <Text>{enc.fechaFin ? formatDate(enc.fechaFin) : 'Sin fecha fin'}</Text>
            </HStack>
            <HStack gap={2} mt={2} align="center">
              <Icon color="gray.400" boxSize={3} asChild><FaUsers /></Icon>
              <Text fontSize="xs" color="gray.500" fontWeight="500">
                {respondidos}/{totalConvocados} respondidos
              </Text>
              <Progress.Root
                value={pct}
                size="xs"
                colorPalette="teal"
                rounded="full"
                w="80px"
                bg="gray.100">
                <Progress.Track>
                  <Progress.Range />
                </Progress.Track>
              </Progress.Root>
            </HStack>
          </Box>
          <HStack gap={1} onClick={(e) => e.stopPropagation()}>
            {enc.estado === 'BORRADOR' && (
              <Tooltip content="Editar" showArrow>
                <IconButton
                  aria-label="Editar"
                  size="sm"
                  variant="ghost"
                  colorPalette="blue"
                  onClick={onEdit}><FaEdit /></IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip content="Eliminar" showArrow>
                <IconButton
                  aria-label="Eliminar"
                  size="sm"
                  variant="ghost"
                  colorPalette="red"
                  onClick={onDelete}><FaTrash /></IconButton>
              </Tooltip>
            )}
          </HStack>
        </Flex>
      </Card.Body>
    </Card.Root>
  );
}

// ─── DetailView ──────────────────────────────────────────────────────────────

function DetailView({
  encuesta: enc, resultados, resultadosLoading, musicos,
  onBack, onEdit, onDelete, onChangeEstado, onCopyLink, onDownloadPdf,
  onUploadImage, onDeleteImage,
  pdfLoading, cardBg, borderColor, subtleBg,
}) {
  const estado = ESTADO_CONFIG[enc.estado] || ESTADO_CONFIG.BORRADOR;
  const shareLink = `${window.location.origin}/consulta/${enc.shareToken}`;

  return (
    <>
      {/* Header */}
      <Button variant="ghost" size="sm" mb={4} onClick={onBack} fontWeight="600"><FaChevronLeft />Volver a encuestas
              </Button>

      <Flex justify="space-between" align="start" mb={6} wrap="wrap" gap={3}>
        <Box>
          <HStack gap={3} mb={1}>
            <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold">{enc.titulo}</Text>
            <Badge colorPalette={estado.color} fontSize="xs" rounded="full" px={2}>
              {estado.label}
            </Badge>
          </HStack>
          {enc.anonima && (
            <Tag.Root size="sm" colorPalette="purple" variant="subtle" rounded="full" mt={1}>
              <Icon mr={1} asChild><FaEye /></Icon> Respuestas anónimas
            </Tag.Root>
          )}
        </Box>
        <HStack gap={2} wrap="wrap">
          {enc.estado === 'BORRADOR' && (
            <>
              <Button
                size="sm"
                colorPalette="green"
                rounded="full"
                onClick={() => onChangeEstado(enc.id, 'ABIERTA')}><FaLockOpen />Abrir encuesta
                              </Button>
              <Button
                size="sm"
                variant="outline"
                colorPalette="blue"
                rounded="full"
                onClick={() => onEdit(enc)}><FaEdit />Editar
                              </Button>
            </>
          )}
          {enc.estado === 'ABIERTA' && (
            <Button
              size="sm"
              colorPalette="red"
              rounded="full"
              onClick={() => onChangeEstado(enc.id, 'CERRADA')}><FaLock />Cerrar encuesta
                          </Button>
          )}
          {enc.estado !== 'CERRADA' && (
            <Button
              size="sm"
              variant="outline"
              colorPalette="red"
              rounded="full"
              onClick={() => onDelete(enc.id)}><FaTrash />Eliminar
                          </Button>
          )}
        </HStack>
      </Flex>

      {/* Tabs */}
      <Tabs.Root variant='subtle' colorPalette="teal" size="sm" defaultValue="0">
        <Tabs.List mb={4} flexWrap="wrap" gap={1}>
          <Tab value="0" fontWeight="600">Info</Tab>
          <Tab value="1" fontWeight="600">Convocados</Tab>
          <Tab value="2" fontWeight="600">Resultados</Tab>
        </Tabs.List>
        <TabPanels>
          <TabPanel value="0" px={0}>
            <InfoTab
              enc={enc}
              shareLink={shareLink}
              onCopyLink={onCopyLink}
              onUploadImage={onUploadImage}
              onDeleteImage={onDeleteImage}
              cardBg={cardBg}
              borderColor={borderColor}
            />
          </TabPanel>
          <TabPanel value="1" px={0}>
            <ConvocadosTab
              enc={enc}
              musicos={musicos}
              onCopyLink={onCopyLink}
              cardBg={cardBg}
              borderColor={borderColor}
            />
          </TabPanel>
          <TabPanel value="2" px={0}>
            <ResultadosTab
              enc={enc}
              resultados={resultados}
              resultadosLoading={resultadosLoading}
              onDownloadPdf={onDownloadPdf}
              pdfLoading={pdfLoading}
              cardBg={cardBg}
              borderColor={borderColor}
              subtleBg={subtleBg}
            />
          </TabPanel>
        </TabPanels>
      </Tabs.Root>
    </>
  );
}

// ─── InfoTab ─────────────────────────────────────────────────────────────────

function InfoTab({ enc, shareLink, onCopyLink, onUploadImage, onDeleteImage, cardBg, borderColor }) {
  const imageInputRef = React.useRef();
  const uploadTargetRef = React.useRef(null);
  const isBorrador = enc.estado === 'BORRADOR';

  const triggerUpload = (type, id) => {
    uploadTargetRef.current = { type, id };
    imageInputRef.current?.click();
  };
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    const target = uploadTargetRef.current;
    if (!file || !target) return;
    await onUploadImage(target.type, target.id, file);
    e.target.value = '';
    uploadTargetRef.current = null;
  };
  return (
    <VStack gap={4} align="stretch">
      <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderColor} rounded="xl">
        <Card.Body>
          <VStack gap={3} align="stretch">
            <Box>
              <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
                Título
              </Text>
              <Text fontWeight="600">{enc.titulo}</Text>
            </Box>
            {enc.introduccion && (
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
                  Introducción
                </Text>
                <Text fontSize="sm" whiteSpace="pre-wrap">{enc.introduccion}</Text>
              </Box>
            )}
            <Separator />
            <HStack gap={6} wrap="wrap">
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Fecha inicio</Text>
                <Text fontSize="sm">{formatDate(enc.fechaInicio)}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Fecha fin</Text>
                <Text fontSize="sm">{enc.fechaFin ? formatDate(enc.fechaFin) : 'Sin fecha fin'}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Anónima</Text>
                <Text fontSize="sm">{enc.anonima ? 'Sí' : 'No'}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Contraseña</Text>
                <Text fontSize="sm" fontFamily="mono">{enc.password || 'Sin contraseña'}</Text>
              </Box>
            </HStack>
            <Separator />
            <Box>
              <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
                Preguntas ({(enc.preguntas || []).length})
              </Text>
              <VStack gap={3} align="stretch" mt={1}>
                {(enc.preguntas || []).map((p, i) => (
                  <Box key={p.id || i} borderWidth="1px" borderColor={borderColor} rounded="lg" p={3}>
                    <Flex justify="space-between" align="start" mb={1}>
                      <HStack gap={2} align="start">
                        <Badge colorPalette="teal" fontSize="2xs" rounded="full" mt={0.5}>{i + 1}</Badge>
                        <Box>
                          <Text fontSize="sm" fontWeight="500">{p.texto}</Text>
                          <Text fontSize="2xs" color="gray.400">
                            {TIPO_PREGUNTA_LABELS[p.tipoPregunta] || p.tipoPregunta}
                            {p.obligatoria ? ' · Obligatoria' : ' · Opcional'}
                          </Text>
                        </Box>
                      </HStack>
                      {isBorrador && (
                        <Tooltip content="Añadir imagen a la pregunta" showArrow>
                          <IconButton
                            aria-label="Añadir imagen"
                            size="xs"
                            variant="ghost"
                            colorPalette="teal"
                            onClick={() => triggerUpload('pregunta', p.id)}><FaImage /></IconButton>
                        </Tooltip>
                      )}
                    </Flex>
                    {(p.imagenes || []).length > 0 && (
                      <Flex gap={2} wrap="wrap" mt={2}>
                        {p.imagenes.map((img) => (
                          <ThumbWithLightbox key={img.id} src={img.url} size="80px">
                            {isBorrador && (
                              <IconButton
                                aria-label="Eliminar"
                                size="xs"
                                colorPalette="red"
                                position="absolute"
                                top="-6px"
                                right="-6px"
                                rounded="full"
                                minW="20px"
                                h="20px"
                                onClick={(e) => { e.stopPropagation(); onDeleteImage(img.id); }}><FaTimes /></IconButton>
                            )}
                          </ThumbWithLightbox>
                        ))}
                      </Flex>
                    )}
                    {p.tipoPregunta !== 'TEXTO_LIBRE' && (p.opciones || []).length > 0 && (
                      <VStack gap={1} align="stretch" mt={2} pl={6}>
                        {p.opciones.map((o, oIdx) => (
                          <Box key={o.id || oIdx}>
                            <Flex align="center" gap={2}>
                              <Text fontSize="xs" color="gray.400">{oIdx + 1}.</Text>
                              <Text fontSize="sm">{o.texto}</Text>
                              {isBorrador && (
                                <Tooltip content="Añadir imagen a la opción" showArrow>
                                  <IconButton
                                    aria-label="Imagen opción"
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="purple"
                                    onClick={() => triggerUpload('opcion', o.id)}><FaImage /></IconButton>
                                </Tooltip>
                              )}
                            </Flex>
                            {(o.imagenes || []).length > 0 && (
                              <Flex gap={2} wrap="wrap" mt={1} ml={5}>
                                {o.imagenes.map((img) => (
                                  <ThumbWithLightbox key={img.id} src={img.url} size="60px" borderRadius="6px">
                                    {isBorrador && (
                                      <IconButton
                                        aria-label="Eliminar"
                                        size="xs"
                                        colorPalette="red"
                                        position="absolute"
                                        top="-6px"
                                        right="-6px"
                                        rounded="full"
                                        minW="18px"
                                        h="18px"
                                        fontSize="8px"
                                        onClick={(e) => { e.stopPropagation(); onDeleteImage(img.id); }}><FaTimes /></IconButton>
                                    )}
                                  </ThumbWithLightbox>
                                ))}
                              </Flex>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                ))}
              </VStack>
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Box>
          </VStack>
        </Card.Body>
      </Card.Root>

      {/* Share link */}
      {enc.shareToken && (
        <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderColor} rounded="xl">
          <Card.Body>
            <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
              Enlace general de acceso
            </Text>
            {isBorrador ? (
              <Alert.Root status="info" rounded="lg" fontSize="sm" variant="subtle">
                <Alert.Indicator />
                <Text fontSize="xs">
                  Los enlaces estarán disponibles cuando abras la encuesta. Mientras esté en borrador, los músicos no podrán acceder.
                </Text>
              </Alert.Root>
            ) : (
              <>
                <InputGroup size="sm">
                  <Input
                    value={shareLink}
                    readOnly
                    fontSize="xs"
                    rounded="lg"
                    pr="3rem"
                    bg="gray.50"
                    _dark={{ bg: 'gray.700' }}
                  />
                  <InputRightElement>
                    <Tooltip content="Copiar enlace" showArrow>
                      <IconButton
                        aria-label="Copiar"
                        size="xs"
                        variant="ghost"
                        colorPalette="teal"
                        onClick={() => onCopyLink(shareLink)}><FaCopy /></IconButton>
                    </Tooltip>
                  </InputRightElement>
                </InputGroup>
                <Text fontSize="2xs" color="gray.400" mt={1}>
                  Los músicos acceden con este enlace, se identifican{enc.password ? ' e introducen la contraseña' : ''} y responden.
                </Text>
              </>
            )}
          </Card.Body>
        </Card.Root>
      )}
    </VStack>
  );
}

// ─── ConvocadosTab ───────────────────────────────────────────────────────────

function ConvocadosTab({ enc, musicos, onCopyLink, cardBg, borderColor }) {
  const rawConvocados = enc.convocados || [];
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const isBorrador = enc.estado === 'BORRADOR';

  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [orden, setOrden] = useState('original');

  const convocados = useMemo(() => {
    let list = rawConvocados.map((c) => {
      const m = musicos.find((x) => x.id === (c.musicoId || c.musico?.id));
      const nombre = m
        ? `${m.nombre} ${m.apellidos || ''}`.trim()
        : (c.musico ? `${c.musico.nombre} ${c.musico.apellidos || ''}`.trim() : 'Músico');
      return { ...c, _nombre: nombre };
    });

    if (filtroEstado === 'respondido') list = list.filter((c) => c.respondido);
    else if (filtroEstado === 'pendiente') list = list.filter((c) => !c.respondido);

    if (orden === 'az') list = [...list].sort((a, b) => a._nombre.localeCompare(b._nombre, 'es'));
    else if (orden === 'za') list = [...list].sort((a, b) => b._nombre.localeCompare(a._nombre, 'es'));

    return list;
  }, [rawConvocados, musicos, filtroEstado, orden]);

  return (
    <VStack gap={3} align="stretch">
      {isBorrador && (
        <Alert.Root status="info" rounded="lg" fontSize="sm" variant="subtle">
          <Alert.Indicator />
          <Text fontSize="xs">
            Los enlaces personalizados estarán disponibles cuando abras la encuesta.
          </Text>
        </Alert.Root>
      )}

      <Flex gap={3} wrap="wrap">
        <NativeSelect.Root>
          <NativeSelect.Field
            size="sm"
            rounded="full"
            maxW="200px"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="todos">Todos los estados</option>
            <option value="respondido">Respondido</option>
            <option value="pendiente">Pendiente</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        <NativeSelect.Root>
          <NativeSelect.Field
            size="sm"
            rounded="full"
            maxW="200px"
            value={orden}
            onChange={(e) => setOrden(e.target.value)}>
            <option value="original">Orden original</option>
            <option value="az">A → Z</option>
            <option value="za">Z → A</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Flex>

      <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderColor} rounded="xl" overflow="hidden">
        <Box overflowX="auto">
          <Flex
            bg={headerBg}
            px={4}
            py={3}
            borderBottom="1px"
            borderColor={borderColor}
            minW="500px"
            fontWeight="700"
            fontSize="xs"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            <Box flex={2}>Nombre</Box>
            <Box flex={1} textAlign="center">Estado</Box>
            {!isBorrador && <Box flex={2} textAlign="center">Link personalizado</Box>}
          </Flex>
          {convocados.length === 0 ? (
            <Flex justify="center" py={8}>
              <Text fontSize="sm" color="gray.400">
                {filtroEstado !== 'todos' ? 'No hay convocados con ese estado' : 'No hay convocados'}
              </Text>
            </Flex>
          ) : (
            convocados.map((c) => {
              const link = c.token ? `${window.location.origin}/consulta/r/${c.token}` : '';
              return (
                <Flex
                  key={c.id}
                  px={4}
                  py={3}
                  borderBottom="1px"
                  borderColor={borderColor}
                  minW="500px"
                  align="center"
                  _hover={{ bg: headerBg }}
                  transition="background 0.15s"
                >
                  <Box flex={2}>
                    <Text fontSize="sm" fontWeight="500">{c._nombre}</Text>
                  </Box>
                  <Box flex={1} textAlign="center">
                    {c.respondido ? (
                      <Badge colorPalette="green" fontSize="2xs" rounded="full">Respondido</Badge>
                    ) : (
                      <Badge colorPalette="orange" fontSize="2xs" rounded="full">Pendiente</Badge>
                    )}
                  </Box>
                  {!isBorrador && (
                    <Box flex={2} textAlign="center">
                      {link ? (
                        <Tooltip content={link} showArrow fontSize="2xs" positioning={{
                          placement: "top"
                        }}>
                          <IconButton
                            aria-label="Copiar link"
                            size="xs"
                            variant="ghost"
                            colorPalette="teal"
                            onClick={() => onCopyLink(link)}><FaCopy /></IconButton>
                        </Tooltip>
                      ) : (
                        <Text fontSize="xs" color="gray.400">—</Text>
                      )}
                    </Box>
                  )}
                </Flex>
              );
            })
          )}
        </Box>
      </Card.Root>
    </VStack>
  );
}

// ─── ResultadosTab ───────────────────────────────────────────────────────────

function ResultadosTab({
  enc, resultados, resultadosLoading, onDownloadPdf,
  pdfLoading, cardBg, borderColor, subtleBg,
}) {
  if (resultadosLoading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner size="lg" color="teal.500" borderWidth="3px" />
      </Flex>
    );
  }

  const preguntas = resultados?.resultados || [];

  if (!resultados || preguntas.length === 0) {
    return (
      <Flex direction="column" align="center" py={10}>
        <Icon boxSize={10} color="gray.300" mb={3} asChild><FaPoll /></Icon>
        <Text color="gray.400" fontSize="sm">Aún no hay resultados disponibles</Text>
      </Flex>
    );
  }

  const totalConvocados = resultados.totalConvocados ?? enc.convocados?.length ?? 0;
  const totalRespondidos = resultados.totalRespondidos ?? 0;

  return (
    <VStack gap={4} align="stretch">
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <HStack gap={3}>
          <Tag.Root size="md" colorPalette="teal" variant="subtle" rounded="full" fontWeight="600">
            {totalRespondidos}/{totalConvocados} respuestas
          </Tag.Root>
          <Progress.Root
            value={totalConvocados > 0 ? (totalRespondidos / totalConvocados) * 100 : 0}
            size="sm"
            colorPalette="teal"
            rounded="full"
            w="120px"
            bg="gray.100">
            <Progress.Track>
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
        </HStack>
        <Button
          size="sm"
          colorPalette="red"
          variant="outline"
          rounded="full"
          onClick={() => onDownloadPdf(enc.id)}
          loading={pdfLoading}
          loadingText="Descargando..."><FaFilePdf />Descargar PDF
                  </Button>
      </Flex>

      {preguntas.map((p, i) => (
        <PreguntaResultCard
          key={p.preguntaId || i}
          pregunta={p}
          index={i}
          anonima={enc.anonima}
          cardBg={cardBg}
          borderColor={borderColor}
          subtleBg={subtleBg}
        />
      ))}
    </VStack>
  );
}

function PreguntaResultCard({ pregunta, index, anonima, cardBg, borderColor, subtleBg }) {
  const barBg = useColorModeValue('gray.100', 'gray.600');
  const totalVotos = (pregunta.opciones || []).reduce((sum, o) => sum + (o.count || 0), 0);

  return (
    <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderColor} rounded="xl">
      <Card.Body>
        <HStack gap={2} mb={3}>
          <Badge colorPalette="teal" fontSize="2xs" rounded="full">{index + 1}</Badge>
          <Text fontWeight="600" fontSize="sm">{pregunta.texto}</Text>
          <Badge colorPalette="gray" variant="subtle" fontSize="2xs">
            {TIPO_PREGUNTA_LABELS[pregunta.tipoPregunta] || pregunta.tipoPregunta}
          </Badge>
        </HStack>

        {(pregunta.imagenes || []).length > 0 && (
          <HStack gap={2} mb={3} flexWrap="wrap">
            {pregunta.imagenes.map((img) => (
              <ThumbWithLightbox key={img.id} src={img.url} size="60px" />
            ))}
          </HStack>
        )}

        {pregunta.tipoPregunta === 'TEXTO_LIBRE' ? (
          <VStack gap={2} align="stretch">
            {(pregunta.textosLibres || []).length === 0 ? (
              <Text fontSize="xs" color="gray.400">Sin respuestas</Text>
            ) : (
              (pregunta.textosLibres || []).map((r, ri) => (
                <Box key={ri} bg={subtleBg} rounded="lg" p={3}>
                  <Text fontSize="sm" whiteSpace="pre-wrap">{r.texto}</Text>
                  {!anonima && r.musico && (
                    <Text fontSize="2xs" color="gray.400" mt={1}>— {r.musico}</Text>
                  )}
                </Box>
              ))
            )}
          </VStack>
        ) : (
          <VStack gap={2} align="stretch">
            {(pregunta.opciones || []).map((o, oi) => {
              const pct = totalVotos > 0 ? Math.round((o.count / totalVotos) * 100) : 0;
              return (
                <Box key={oi}>
                  <Flex justify="space-between" mb={1}>
                    <HStack gap={2}>
                      <Text fontSize="sm" fontWeight="500">{o.texto}</Text>
                    </HStack>
                    <HStack gap={2}>
                      <Text fontSize="xs" fontWeight="600" color="teal.600">{pct}%</Text>
                      <Text fontSize="2xs" color="gray.400">({o.count || 0})</Text>
                    </HStack>
                  </Flex>
                  <Box bg={barBg} rounded="full" h="8px" overflow="hidden">
                    <Box
                      bg="teal.400"
                      h="full"
                      rounded="full"
                      w={`${pct}%`}
                      transition="width 0.4s ease"
                    />
                  </Box>
                  {(o.imagenes || []).length > 0 && (
                    <HStack gap={2} mt={2} flexWrap="wrap">
                      {o.imagenes.map((img) => (
                        <ThumbWithLightbox key={img.id} src={img.url} size="50px" />
                      ))}
                    </HStack>
                  )}
                </Box>
              );
            })}
          </VStack>
        )}
      </Card.Body>
    </Card.Root>
  );
}

// ─── FormModal ───────────────────────────────────────────────────────────────

const WIZARD_STEPS = [
  { label: 'Configuración', icon: FaEdit },
  { label: 'Preguntas', icon: FaPoll },
  { label: 'Convocados', icon: FaUsers },
];

function FormModal({
  isOpen, onClose, form, setField, formTab, setFormTab,
  editingId, saving, onSave, musicos, gruposMusicos,
  preguntas, addPregunta, removePregunta, updatePregunta, movePregunta,
  addOpcion, removeOpcion, updateOpcion,
  addImageToPregunta, removeImageFromPregunta, removeExistingImageFromPregunta,
  addImageToOpcion, removeImageFromOpcion, removeExistingImageFromOpcion,
}) {
  
  const stepBg = useColorModeValue('gray.100', 'gray.700');
  const stepActiveBg = useColorModeValue('teal.500', 'teal.300');
  const stepActiveColor = useColorModeValue('white', 'gray.900');
  const stepDoneBg = useColorModeValue('teal.100', 'teal.800');
  const stepDoneColor = useColorModeValue('teal.700', 'teal.200');
  const stepTextColor = useColorModeValue('gray.500', 'gray.400');
  const stepLineColor = useColorModeValue('gray.300', 'gray.600');
  const stepLineDone = useColorModeValue('teal.300', 'teal.600');

  const canGoNext = () => {
    if (formTab === 0) {
      return form.titulo.trim() && form.fechaInicio;
    }
    if (formTab === 1) {
      if (form.preguntas.length === 0) return false;
      return form.preguntas.every((p) => {
        if (!p.texto.trim()) return false;
        if (p.tipoPregunta !== 'TEXTO_LIBRE') {
          const validOpts = (p.opciones || []).filter((o) => o.texto.trim());
          if (validOpts.length < 2) return false;
        }
        return true;
      });
    }
    return true;
  };

  const handleNext = () => {
    if (formTab === 0) {
      if (!form.titulo.trim()) {
        toaster.create({ title: 'El título es obligatorio', type: 'warning', duration: 2500 });
        return;
      }
      if (!form.fechaInicio) {
        toaster.create({ title: 'La fecha de inicio es obligatoria', type: 'warning', duration: 2500 });
        return;
      }
    }
    if (formTab === 1) {
      if (form.preguntas.length === 0) {
        toaster.create({ title: 'Añade al menos una pregunta', type: 'warning', duration: 2500 });
        return;
      }
      for (let i = 0; i < form.preguntas.length; i++) {
        const p = form.preguntas[i];
        if (!p.texto.trim()) {
          toaster.create({ title: `La pregunta ${i + 1} no tiene texto`, type: 'warning', duration: 2500 });
          return;
        }
        if (p.tipoPregunta !== 'TEXTO_LIBRE') {
          const valid = (p.opciones || []).filter((o) => o.texto.trim());
          if (valid.length < 2) {
            toaster.create({ title: `La pregunta ${i + 1} necesita al menos 2 opciones`, type: 'warning', duration: 2500 });
            return;
          }
        }
      }
    }
    setFormTab((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handlePrev = () => setFormTab((prev) => Math.max(prev - 1, 0));

  const handleFinalSave = () => {
    if (form.convocados.length === 0) {
      toaster.create({ title: 'Selecciona al menos un músico', type: 'warning', duration: 2500 });
      return;
    }
    onSave();
  };

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" closeOnInteractOutside={false} onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content rounded="2xl" maxH="90vh">
            <Dialog.Header fontWeight="700" pb={0}>
              {editingId ? 'Editar encuesta' : 'Crear encuesta'}
            </Dialog.Header>
            <Dialog.CloseTrigger />

            {/* Stepper */}
            <Box px={6} pt={2} pb={4}>
              <Flex align="center" justify="center">
                {WIZARD_STEPS.map((s, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <Box flex={1} h="2px" bg={i <= formTab ? stepLineDone : stepLineColor} mx={1} rounded="full" />
                    )}
                    <Flex
                      direction="column"
                      align="center"
                      cursor={i < formTab ? 'pointer' : 'default'}
                      onClick={() => { if (i < formTab) setFormTab(i); }}
                      opacity={i <= formTab ? 1 : 0.5}
                      minW="70px"
                    >
                      <Flex
                        w="32px" h="32px" rounded="full" align="center" justify="center"
                        bg={i === formTab ? stepActiveBg : i < formTab ? stepDoneBg : stepBg}
                        color={i === formTab ? stepActiveColor : i < formTab ? stepDoneColor : stepTextColor}
                        transition="all 0.2s"
                        fontSize="sm"
                      >
                        {i < formTab ? <Icon boxSize="12px" asChild><FaCheck /></Icon> : <Icon boxSize="12px" asChild><s.icon /></Icon>}
                      </Flex>
                      <Text
                        fontSize="2xs"
                        fontWeight={i === formTab ? '700' : '500'}
                        color={i === formTab ? 'teal.500' : stepTextColor}
                        mt={1}
                        textAlign="center"
                      >
                        {s.label}
                      </Text>
                    </Flex>
                  </React.Fragment>
                ))}
              </Flex>
            </Box>

            <Dialog.Body pb={2} pt={0}>
              {formTab === 0 && <ConfigTab form={form} setField={setField} />}
              {formTab === 1 && (
                <PreguntasTab
                  preguntas={preguntas}
                  addPregunta={addPregunta}
                  removePregunta={removePregunta}
                  updatePregunta={updatePregunta}
                  movePregunta={movePregunta}
                  addOpcion={addOpcion}
                  removeOpcion={removeOpcion}
                  updateOpcion={updateOpcion}
                  addImageToPregunta={addImageToPregunta}
                  removeImageFromPregunta={removeImageFromPregunta}
                  removeExistingImageFromPregunta={removeExistingImageFromPregunta}
                  addImageToOpcion={addImageToOpcion}
                  removeImageFromOpcion={removeImageFromOpcion}
                  removeExistingImageFromOpcion={removeExistingImageFromOpcion}
                />
              )}
              {formTab === 2 && (
                <ConvocadosFormTab
                  form={form}
                  setField={setField}
                  musicos={musicos}
                  gruposMusicos={gruposMusicos}
                />
              )}
            </Dialog.Body>

            <Dialog.Footer borderTop="1px" borderColor="gray.100">
              <Button variant="ghost" mr="auto" onClick={onClose} size="sm">
                Cancelar
              </Button>
              {formTab > 0 && (
                <Button variant="outline" mr={3} onClick={handlePrev} size="sm" rounded="full"><FaChevronLeft />Anterior
                              </Button>
              )}
              {formTab < WIZARD_STEPS.length - 1 ? (
                <Button colorPalette="teal" onClick={handleNext} size="sm" rounded="full">Siguiente
                              <FaChevronRight /></Button>
              ) : (
                <Button
                  colorPalette="teal"
                  onClick={handleFinalSave}
                  loading={saving}
                  loadingText="Guardando..."
                  rounded="full"
                  size="sm"><FaCheck />{editingId ? 'Guardar cambios' : 'Crear encuesta'}</Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}

// ─── ConfigTab ───────────────────────────────────────────────────────────────

function ConfigTab({ form, setField }) {
  return (
    <VStack gap={4} align="stretch">
      <Field.Root required>
        <Field.Label fontSize="sm">Título</Field.Label>
        <Input
          value={form.titulo}
          onChange={(e) => setField('titulo', e.target.value)}
          placeholder="Ej: Elección de uniforme para la temporada"
          size="sm"
          rounded="lg"
        />
      </Field.Root>

      <Field.Root>
        <Field.Label fontSize="sm">Introducción</Field.Label>
        <Textarea
          value={form.introduccion}
          onChange={(e) => setField('introduccion', e.target.value)}
          placeholder="Texto informativo que verán los músicos antes de las preguntas"
          size="sm"
          rounded="lg"
          rows={3}
        />
      </Field.Root>

      <HStack gap={4}>
        <Field.Root required flex={1}>
          <Field.Label fontSize="sm">Fecha inicio</Field.Label>
          <Input
            type="date"
            value={form.fechaInicio}
            onChange={(e) => setField('fechaInicio', e.target.value)}
            size="sm"
            rounded="lg"
          />
        </Field.Root>
        <Field.Root flex={1}>
          <Field.Label fontSize="sm">Fecha fin</Field.Label>
          <Input
            type="date"
            value={form.fechaFin}
            onChange={(e) => setField('fechaFin', e.target.value)}
            size="sm"
            rounded="lg"
          />
          <Text fontSize="2xs" color="gray.400" mt={1}>
            Dejar vacío para que se mantenga abierta indefinidamente
          </Text>
        </Field.Root>
      </HStack>

      <Field.Root>
        <Field.Label fontSize="sm">Contraseña de acceso</Field.Label>
        <Input
          value={form.password}
          onChange={(e) => setField('password', e.target.value)}
          placeholder="Contraseña para acceder desde el enlace general"
          size="sm"
          rounded="lg"
        />
        <Text fontSize="2xs" color="gray.400" mt={1}>
          Los músicos la introducirán al acceder por el enlace general. Déjala vacía para acceso sin contraseña.
        </Text>
      </Field.Root>

      <Field.Root display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Field.Label mb={0} fontSize="sm">Anónima</Field.Label>
          <Text fontSize="2xs" color="gray.400">
            Si se activa, las respuestas serán anónimas. Los músicos verán un aviso al responder.
          </Text>
        </Box>
        <Switch
          checked={form.anonima}
          onChange={(e) => setField('anonima', e.target.checked)}
          colorPalette="purple"
        />
      </Field.Root>
    </VStack>
  );
}

// ─── PreguntasTab ────────────────────────────────────────────────────────────

function ImageThumbnails({ files, existingImages, onRemoveFile, onRemoveExisting }) {
  const all = [
    ...(existingImages || []).map((img) => ({ type: 'existing', ...img })),
    ...(files || []).map((f, i) => ({ type: 'local', file: f, idx: i })),
  ];
  if (all.length === 0) return null;
  return (
    <Flex gap={2} wrap="wrap" mt={2}>
      {all.map((item, i) => (
        <Box key={item.type === 'existing' ? item.id : `local-${item.idx}`} position="relative" w="60px" h="60px">
          <img
            src={item.type === 'existing' ? item.url : URL.createObjectURL(item.file)}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }}
          />
          <IconButton
            aria-label="Eliminar"
            size="xs"
            colorPalette="red"
            position="absolute"
            top="-5px"
            right="-5px"
            rounded="full"
            minW="18px"
            h="18px"
            fontSize="8px"
            onClick={() => {
              if (item.type === 'existing') onRemoveExisting?.(item.id);
              else onRemoveFile?.(item.idx);
            }}><FaTimes /></IconButton>
        </Box>
      ))}
    </Flex>
  );
}

function PreguntasTab({
  preguntas, addPregunta, removePregunta, updatePregunta, movePregunta,
  addOpcion, removeOpcion, updateOpcion,
  addImageToPregunta, removeImageFromPregunta, removeExistingImageFromPregunta,
  addImageToOpcion, removeImageFromOpcion, removeExistingImageFromOpcion,
}) {
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const imgBg = useColorModeValue('gray.50', 'gray.700');
  const preguntaInputRef = useRef();
  const opcionInputRef = useRef();
  const uploadCtxRef = useRef(null);

  const triggerPreguntaUpload = (pIdx) => {
    uploadCtxRef.current = { type: 'pregunta', pIdx };
    preguntaInputRef.current.value = '';
    preguntaInputRef.current.click();
  };

  const triggerOpcionUpload = (pIdx, oIdx) => {
    uploadCtxRef.current = { type: 'opcion', pIdx, oIdx };
    opcionInputRef.current.value = '';
    opcionInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    const ctx = uploadCtxRef.current;
    if (!file || !ctx) return;
    if (ctx.type === 'pregunta') addImageToPregunta(ctx.pIdx, file);
    else addImageToOpcion(ctx.pIdx, ctx.oIdx, file);
    uploadCtxRef.current = null;
  };

  return (
    <VStack gap={4} align="stretch">
      {preguntas.length === 0 && (
        <Alert.Root status="info" rounded="lg" fontSize="sm">
          <Alert.Indicator />
          Añade al menos una pregunta para tu encuesta.
        </Alert.Root>
      )}

      {preguntas.map((p, idx) => (
        <Box key={p._key} border="1px" borderColor={cardBorder} rounded="xl" p={4}>
          <Flex justify="space-between" align="center" mb={3}>
            <HStack gap={2}>
              <Badge colorPalette="teal" fontSize="xs" rounded="full" px={2}>
                Pregunta {idx + 1}
              </Badge>
              {p.obligatoria && (
                <Badge colorPalette="orange" variant="subtle" fontSize="2xs">Obligatoria</Badge>
              )}
            </HStack>
            <HStack gap={1}>
              <Tooltip content="Subir" showArrow>
                <IconButton
                  aria-label="Subir"
                  size="xs"
                  variant="ghost"
                  disabled={idx === 0}
                  onClick={() => movePregunta(idx, -1)}><FaArrowUp /></IconButton>
              </Tooltip>
              <Tooltip content="Bajar" showArrow>
                <IconButton
                  aria-label="Bajar"
                  size="xs"
                  variant="ghost"
                  disabled={idx === preguntas.length - 1}
                  onClick={() => movePregunta(idx, 1)}><FaArrowDown /></IconButton>
              </Tooltip>
              <Tooltip content="Eliminar pregunta" showArrow>
                <IconButton
                  aria-label="Eliminar"
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => removePregunta(idx)}><FaTrash /></IconButton>
              </Tooltip>
            </HStack>
          </Flex>

          <VStack gap={3} align="stretch">
            <Field.Root>
              <Input
                value={p.texto}
                onChange={(e) => updatePregunta(idx, 'texto', e.target.value)}
                placeholder="Escribe la pregunta..."
                size="sm" rounded="lg" fontWeight="500"
              />
            </Field.Root>

            <HStack gap={3}>
              <Field.Root flex={1}>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={p.tipoPregunta}
                    onChange={(e) => updatePregunta(idx, 'tipoPregunta', e.target.value)}
                    size="sm"
                    rounded="lg">
                    <option value="SELECCION_UNICA">Selección única</option>
                    <option value="SELECCION_MULTIPLE">Selección múltiple</option>
                    <option value="TEXTO_LIBRE">Texto libre</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root display="flex" alignItems="center" w="auto">
                <Field.Label mb={0} fontSize="xs" whiteSpace="nowrap">Obligatoria</Field.Label>
                <Switch checked={p.obligatoria}
                  onChange={(e) => updatePregunta(idx, 'obligatoria', e.target.checked)}
                  colorPalette="orange" size="sm" />
              </Field.Root>
            </HStack>

            {/* Question images */}
            <Box bg={imgBg} rounded="lg" p={2}>
              <Flex align="center" gap={2} mb={((p._imagenes || []).length + (p._existingImages || []).length) > 0 ? 0 : undefined}>
                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette="teal"
                  onClick={() => triggerPreguntaUpload(idx)}><FaImage />Añadir imagen a la pregunta
                                  </Button>
              </Flex>
              <ImageThumbnails
                files={p._imagenes}
                existingImages={p._existingImages}
                onRemoveFile={(imgIdx) => removeImageFromPregunta(idx, imgIdx)}
                onRemoveExisting={(imgId) => removeExistingImageFromPregunta(idx, imgId)}
              />
            </Box>

            {p.tipoPregunta !== 'TEXTO_LIBRE' && (
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>Opciones</Text>
                <VStack gap={3} align="stretch">
                  {(p.opciones || []).map((o, oIdx) => (
                    <Box key={oIdx} borderLeft="2px" borderColor="teal.200" pl={3}>
                      <HStack gap={2}>
                        <Text fontSize="xs" color="gray.400" minW="20px" textAlign="right">{oIdx + 1}.</Text>
                        <Input value={o.texto}
                          onChange={(e) => updateOpcion(idx, oIdx, e.target.value)}
                          placeholder={`Opción ${oIdx + 1}`}
                          size="sm" rounded="lg" flex={1} />
                        <Tooltip content="Añadir imagen" showArrow>
                          <IconButton
                            aria-label="Imagen"
                            size="xs"
                            variant="ghost"
                            colorPalette="purple"
                            onClick={() => triggerOpcionUpload(idx, oIdx)}><FaImage /></IconButton>
                        </Tooltip>
                        {(p.opciones || []).length > 2 && (
                          <Tooltip content="Eliminar opción" showArrow>
                            <IconButton
                              aria-label="Quitar"
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() => removeOpcion(idx, oIdx)}><FaTimes /></IconButton>
                          </Tooltip>
                        )}
                      </HStack>
                      <ImageThumbnails
                        files={o._imagenes}
                        existingImages={o._existingImages}
                        onRemoveFile={(imgIdx) => removeImageFromOpcion(idx, oIdx, imgIdx)}
                        onRemoveExisting={(imgId) => removeExistingImageFromOpcion(idx, oIdx, imgId)}
                      />
                    </Box>
                  ))}
                  <Button
                    size="xs"
                    variant="ghost"
                    colorPalette="teal"
                    onClick={() => addOpcion(idx)}
                    alignSelf="start"><FaPlus />Añadir opción
                                      </Button>
                </VStack>
              </Box>
            )}
          </VStack>
        </Box>
      ))}

      <Button
        colorPalette="teal"
        variant="outline"
        size="sm"
        rounded="full"
        onClick={addPregunta}
        alignSelf="center"><FaPlus />Añadir pregunta
              </Button>

      <input type="file" accept="image/*" ref={preguntaInputRef} style={{ display: 'none' }}
        onChange={handleFileChange} />
      <input type="file" accept="image/*" ref={opcionInputRef} style={{ display: 'none' }}
        onChange={handleFileChange} />
    </VStack>
  );
}

// ─── ConvocadosFormTab ───────────────────────────────────────────────────────

function ConvocadosFormTab({ form, setField, musicos, gruposMusicos }) {
  return (
    <VStack gap={3} align="stretch">
      {musicos.length === 0 ? (
        <Alert.Root status="warning" rounded="lg" fontSize="sm">
          <Alert.Indicator />
          No hay músicos registrados. Añade músicos en la sección "Mis músicos".
        </Alert.Root>
      ) : (
        <IsolateCheckboxesFromField>
          <Tabs.Root size="sm" variant='subtle' colorPalette="purple">
            <Tabs.List mb={2}>
              <Tab value="0" fontSize="xs" px={3}>Todos</Tab>
              <Tab value="1" fontSize="xs" px={3}>Grupos de músicos</Tab>
            </Tabs.List>
            <TabPanels>
              {/* Todos */}
              <TabPanel value="0" px={0} py={0}>
                <Box maxH="300px" overflowY="auto" border="1px" borderColor="gray.200" rounded="md" p={2}>
                  <VStack align="start" gap={1}>
                    <HStack justify="space-between" w="full" mb={1}>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="purple"
                        onClick={() => setField('convocados', musicos.map((m) => m.id))}
                      >
                        Todos
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="gray"
                        onClick={() => setField('convocados', [])}
                      >
                        Ninguno
                      </Button>
                    </HStack>
                    {musicos.map((m) => (
                      <Checkbox.Root
                        key={m.id}
                        onCheckedChange={(details) => {
                          setField(
                            'convocados',
                            details.checked === true
                              ? [...form.convocados, m.id]
                              : form.convocados.filter((id) => id !== m.id),
                          );
                        }}
                        colorPalette="purple"
                        size="sm"
                        checked={form.convocados.includes(m.id)}
                      ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                        <Text fontSize="sm">{m.nombre} {m.apellidos || ''}</Text>
                      </Checkbox.Label></Checkbox.Root>
                    ))}
                  </VStack>
                </Box>
              </TabPanel>
              {/* Grupos */}
              <TabPanel value="1" px={0} py={0}>
                <Box maxH="300px" overflowY="auto" border="1px" borderColor="gray.200" rounded="md" p={2}>
                  {gruposMusicos.length === 0 ? (
                    <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>
                      No hay grupos de músicos creados
                    </Text>
                  ) : (
                    <VStack align="start" gap={2}>
                      {gruposMusicos.map((g) => {
                        const memberIds = (g.miembros || [])
                          .map((gm) => gm.musico?.id || gm.musicoId)
                          .filter(Boolean);
                        const activeMemberIds = memberIds.filter((id) =>
                          musicos.some((m) => m.id === id),
                        );
                        const allChecked =
                          activeMemberIds.length > 0 &&
                          activeMemberIds.every((id) => form.convocados.includes(id));
                        const someChecked =
                          !allChecked &&
                          activeMemberIds.some((id) => form.convocados.includes(id));
                        return (
                          <Box key={g.id} w="full">
                            <Checkbox.Root
                              onCheckedChange={(details) => {
                                if (details.checked === true) {
                                  setField(
                                    'convocados',
                                    [...new Set([...form.convocados, ...activeMemberIds])],
                                  );
                                } else {
                                  setField(
                                    'convocados',
                                    form.convocados.filter((id) => !activeMemberIds.includes(id)),
                                  );
                                }
                              }}
                              colorPalette="purple"
                              size="sm"
                              checked={someChecked ? 'indeterminate' : allChecked}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                              <HStack gap={1}>
                                <Text fontSize="sm" fontWeight="600">{g.nombre}</Text>
                                <Badge colorPalette="purple" fontSize="2xs">
                                  {activeMemberIds.length}
                                </Badge>
                              </HStack>
                            </Checkbox.Label></Checkbox.Root>
                            <Text fontSize="2xs" color="gray.500" ml={6}>
                              {activeMemberIds
                                .map((id) => {
                                  const m = musicos.find((x) => x.id === id);
                                  return m ? `${m.nombre} ${m.apellidos || ''}`.trim() : '';
                                })
                                .filter(Boolean)
                                .join(', ')}
                            </Text>
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs.Root>
          <Text fontSize="xs" color="gray.500">
            {form.convocados.length} músico{form.convocados.length !== 1 ? 's' : ''} seleccionado
            {form.convocados.length !== 1 ? 's' : ''}
          </Text>
        </IsolateCheckboxesFromField>
      )}
    </VStack>
  );
}
